import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { IconCash } from '@tabler/icons-react';
import { api } from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useCanEdit } from './RoleGate.jsx';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { LEDGER_TYPE_CATEGORY, LEDGER_TYPE_ICON } from '../utils/ledgerIcon';
import {
  LEDGER_GROUPS,
  calculateLastBillPaid,
  checkingLedgerNames,
  lastBillLinesPath,
  showsLastBillPaid,
} from '../utils/ledgerGroups';
import { byName, groupBudgetsByCategory } from '../utils/budgetGroups';
import { horizonOf } from '../utils/budgetsByCategory';
import { resolveBudgetIcon } from '../utils/budgetIcon';
import { MaskedCurrency } from './MaskedValue';
import { resolveAmountColor } from './AmountDisplay';
import { BalanceSkeleton } from './SkeletonLoader';
import { useTimezoneAwareDateFormatting } from './TimezoneAwareDateDisplay';
import { SectionContainer, Row, HorizonChip } from './ds';
import HorizonSwitchDialog from './HorizonSwitchDialog';

/**
 * Dashboard groups (#1971, canvas screen 8): ledgers by type and budgets by
 * category, each group a collapsible SectionContainer with a category-colored
 * top edge, every group closed on arrival. Grouping rules are shared with the
 * Ledgers and Budgets pages (utils/ledgerGroups, utils/budgetGroups).
 */

/** Column count by viewport, the Budgets page breakpoints: 3 / 2 / 1. */
const useColumnCount = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  return isMobile ? 1 : isWide ? 3 : 2;
};

/**
 * Groups fill the columns left to right in order (closed groups are one bar
 * high, so the rows line up); `trailing` groups (Pending Income, Planned
 * Expenses) go at the end of the last column, as on the Budgets page. Each
 * column stacks on its own, so opening a group never moves another column.
 */
const GroupGrid = ({ groups, trailing = [] }) => {
  const { ds } = useTheme();
  const columnCount = useColumnCount();
  const columns = Array.from({ length: columnCount }, () => []);
  groups.forEach((node, i) => columns[i % columnCount].push(node));
  columns[columnCount - 1].push(...trailing);
  const gap = `${ds.space[6]}px`;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        gap,
        alignItems: 'start',
      }}
    >
      {columns.map((items, i) => (
        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap, minWidth: 0 }}>
          {items}
        </Box>
      ))}
    </Box>
  );
};

/** Uppercase section label over a group grid ("Ledgers", "Budgets"). */
export const GroupSectionLabel = ({ children }) => {
  const { palette, ds } = useTheme();
  return (
    <Typography
      component="h2"
      sx={{
        m: 0,
        mb: `${ds.space[3]}px`,
        fontSize: `${ds.font.meta}px`,
        fontWeight: ds.font.weightRegular,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: palette.text.dim,
      }}
    >
      {children}
    </Typography>
  );
};

const useAmount = () => {
  const { palette } = useTheme();
  // Section 11 coloring through MaskedCurrency, so masking holds and the number
  // keeps the Row / header font scale.
  return (dollars, variant) => {
    const value = typeof dollars === 'number' ? dollars : 0;
    const cents = Math.round(value * 100);
    return (
      <MaskedCurrency
        value={value}
        style={{ color: resolveAmountColor(palette, variant, cents < 0, cents === 0) }}
      />
    );
  };
};

const sumOf = (rows, valueOf) => rows.reduce((sum, r) => {
  const v = valueOf(r);
  return typeof v === 'number' ? sum + v : sum;
}, 0);

const rowNav = (navigate, path) => ({
  onClick: () => navigate(path),
  role: 'button',
  tabIndex: 0,
  onKeyDown: (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(path);
    }
  },
});

/**
 * @param {object} props
 * @param {Array} props.ledgers           active ledgers
 * @param {object} props.balances         `l:<ledgerId>` -> dollars
 * @param {boolean} props.balancesLoading
 * @param {('default'|'compact')} props.density
 * @param {('default'|'compact')} props.rowSize
 * @param {boolean} [props.defaultCollapsed]  false opens every group on arrival
 *                                            and loads "Last bill paid" at once
 *                                            (Dashboard_N, #2139)
 */
export const DashboardLedgerGroups = ({
  ledgers, balances, balancesLoading, density, rowSize, defaultCollapsed = true,
}) => {
  const navigate = useNavigate();
  const { activeGroupId } = useGroup();
  const { formatDate } = useTimezoneAwareDateFormatting();
  const amount = useAmount();
  // "Last bill paid" loads on a group's first open, once per visit (#1971):
  // a closed Dashboard makes no lines requests.
  const [lastBill, setLastBill] = useState({});
  const requested = useRef(new Set());

  const loadLastBill = async (groupLedgers) => {
    const results = await Promise.all(groupLedgers.map((l) => (
      api.get(activeGroupId, lastBillLinesPath(l.ledgerId))
        .then((r) => [l.ledgerId, calculateLastBillPaid(r?.lines || [], checkingLedgerNames(ledgers))])
        .catch(() => [l.ledgerId, null])
    )));
    setLastBill((prev) => ({ ...prev, ...Object.fromEntries(results) }));
  };

  const requestLastBill = (type, billed) => {
    if (billed.length === 0 || requested.current.has(type)) return;
    requested.current.add(type);
    loadLastBill(billed);
  };

  // Open on arrival: every billed group asks for its last bill at once, the
  // same request its first open would make.
  useEffect(() => {
    if (defaultCollapsed) return;
    LEDGER_GROUPS.forEach((group) => {
      requestLastBill(group.type, ledgers.filter((l) => l.type === group.type).filter(showsLastBillPaid));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount, like a first open
  }, []);

  const balanceOf = (l) => balances[`l:${l.ledgerId}`];

  const groups = LEDGER_GROUPS.map((group) => {
    const groupLedgers = ledgers.filter((l) => l.type === group.type);
    if (groupLedgers.length === 0) return null;
    const billed = groupLedgers.filter(showsLastBillPaid);
    const onToggle = (open) => {
      if (open) requestLastBill(group.type, billed);
    };
    return (
      <SectionContainer
        key={group.type}
        data-dashboard-ledger-group={group.type}
        title={group.label}
        count={groupLedgers.length}
        category={group.category}
        density={density}
        edge
        collapsible
        defaultCollapsed={defaultCollapsed}
        onToggle={onToggle}
        subtotal={balancesLoading ? <BalanceSkeleton width="70px" /> : amount(sumOf(groupLedgers, balanceOf), 'subtotal')}
      >
        {groupLedgers.map((l) => {
          const bal = balanceOf(l);
          const Icon = LEDGER_TYPE_ICON[l.type] || IconCash;
          let meta;
          if (showsLastBillPaid(l)) {
            const line = lastBill[l.ledgerId];
            const shown = line === undefined ? '...' : (line ? formatDate(line.dateStamp, 'date-only') : '-');
            meta = `Last bill paid: ${shown}`;
          }
          return (
            <Row
              key={l.ledgerId}
              category={LEDGER_TYPE_CATEGORY[l.type] || 'neutral'}
              icon={<Icon />}
              name={l.name}
              meta={meta}
              size={rowSize}
              value={balancesLoading && bal == null ? <BalanceSkeleton width="60px" /> : amount(bal ?? 0, 'row')}
              aria-label={l.name}
              data-dashboard-ledger={l.ledgerId}
              {...rowNav(navigate, `/ledgers/${l.ledgerId}`)}
            />
          );
        })}
      </SectionContainer>
    );
  }).filter(Boolean);

  return <GroupGrid groups={groups} />;
};

/**
 * @param {object} props
 * @param {Array} props.budgets           active budgets, every type
 * @param {Array} props.budgetCategories
 * @param {function} [props.onBudgetUpdated]  called after a horizon switch saved (#2175); the page reloads its budgets
 * @param {object} props.balances         `b:<budgetId>` -> dollars
 * @param {boolean} props.balancesLoading
 * @param {('default'|'compact')} props.density
 * @param {('default'|'compact')} props.rowSize
 * @param {boolean} [props.defaultCollapsed]  false opens every group on arrival (Dashboard_N, #2139)
 */
export const DashboardBudgetGroups = ({
  budgets, budgetCategories, balances, balancesLoading, density, rowSize, defaultCollapsed = true, onBudgetUpdated,
}) => {
  const navigate = useNavigate();
  const strings = useEngineStrings();
  const amount = useAmount();
  const canEdit = useCanEdit();
  // #2175: the Normal row whose horizon chip was pressed; null keeps the dialog closed.
  const [horizonBudget, setHorizonBudget] = useState(null);

  const rawBalanceOf = (b) => balances[`b:${b.budgetId}`];
  // Pending Income (type 3) reads sign-flipped: a raw negative balance is the
  // positive amount owed (#906).
  const displayBalanceOf = (b) => {
    const raw = rawBalanceOf(b);
    return raw != null && b.type === 3 ? -raw : raw;
  };

  const rowsOf = (members, category) => members.map((b) => {
    const bal = displayBalanceOf(b);
    const BudgetIcon = resolveBudgetIcon(b.icon);
    return (
      <Row
        key={b.budgetId}
        category={category}
        icon={<BudgetIcon />}
        name={b.name}
        size={rowSize}
        trailing={b.type === 0 ? <HorizonChip horizon={horizonOf(b)} onPress={canEdit ? () => setHorizonBudget(b) : undefined} /> : undefined}
        actionsAfterValue
        value={balancesLoading && bal == null ? <BalanceSkeleton width="60px" /> : amount(bal ?? 0, 'row')}
        aria-label={b.name}
        {...rowNav(navigate, `/budgets/${b.budgetId}`)}
      />
    );
  });

  const subtotal = (members) => (
    balancesLoading ? <BalanceSkeleton width="70px" /> : amount(sumOf(members, displayBalanceOf), 'subtotal')
  );

  const groupProps = { density, edge: true, collapsible: true, defaultCollapsed };

  const normal = budgets.filter((b) => b.type === 0);
  const cards = groupBudgetsByCategory(normal, budgetCategories, strings['budgetCategories.default']);
  const groups = cards.map((card) => (
    <SectionContainer
      key={card.key}
      data-dashboard-budget-group={card.key}
      title={card.name}
      count={card.members.length}
      category="budgets"
      subtotal={subtotal(card.members)}
      {...groupProps}
    >
      {rowsOf(card.members, 'budgets')}
    </SectionContainer>
  ));

  const trailing = [];
  const pendingIncome = budgets.filter((b) => b.type === 3).sort(byName);
  if (pendingIncome.length > 0) {
    trailing.push(
      <SectionContainer
        key="pending-income"
        data-dashboard-budget-group="pending-income"
        title="Pending Income"
        count={pendingIncome.length}
        category="income"
        subtotal={subtotal(pendingIncome)}
        {...groupProps}
      >
        {rowsOf(pendingIncome, 'income')}
      </SectionContainer>
    );
  }
  const planned = budgets.filter((b) => b.type === 2);
  if (planned.length > 0) {
    trailing.push(
      <SectionContainer
        key="planned-expenses"
        data-dashboard-budget-group="planned-expenses"
        title="Planned Expenses"
        count={planned.length}
        category="planned"
        note="isolated from totals"
        {...groupProps}
      >
        {rowsOf(planned, 'planned')}
      </SectionContainer>
    );
  }

  return (
    <>
      <GroupGrid groups={groups} trailing={trailing} />
      <HorizonSwitchDialog
        budget={horizonBudget}
        onClose={() => setHorizonBudget(null)}
        onSwitched={() => { setHorizonBudget(null); if (onBudgetUpdated) onBudgetUpdated(); }}
      />
    </>
  );
};
